// Zod schemas at the IO boundary for the Search surface.
//
// **Design goal:** catch wire-shape drift early without crashing
// production. Crustdata occasionally adds/renames fields; the response
// types in our store are best-effort. These schemas validate the
// _shape_ of objects landing from the wire (and from localStorage)
// and emit a console warning in DEV when something doesn't match.
// Production never throws — schemas use ``.safeParse()`` and we ignore
// the failure (the existing TS types treat every field as optional).
//
// Validators are intentionally PERMISSIVE:
//   - every field optional, since Crustdata strips empties
//   - ``passthrough()`` on objects so new fields don't trigger spam
//   - ``raw`` accepted as any record (we don't validate the Crustdata
//     raw subtree — its shape is the upstream's domain)
//
// What we DO catch:
//   - core fields with the wrong primitive (id as object, total_count
//     as string, etc.)
//   - SavedSearch entries that drifted from the {id, name, searchType,
//     filters, createdAt} contract
//
// Adding a stricter schema later is fine — drop the ``.passthrough()``
// or tighten a field type and the existing call sites pick it up.

import { z } from 'zod';

const isDev = (): boolean => {
  try {
    return import.meta.env.DEV === true;
  } catch {
    return false;
  }
};

/** Internal helper: ``safeParse`` + DEV-only console warn. Never throws
 *  in production. Returns the (un-)validated value so callers can chain
 *  without writing the same try/catch over and over. */
function tolerantParse<T>(
  schema: z.ZodType<T>,
  value: unknown,
  label: string
): T | unknown {
  // Production: skip the parse entirely. These schemas are deliberately
  // permissive (every field optional, ``passthrough()``), so a successful
  // parse returns a value equivalent to the input, and we ignore failures
  // anyway — the ONLY product of validation is the DEV console warning. A
  // people search returns ~100 rows each carrying a deep raw Crustdata blob,
  // so the Zod walk is ~100-200 ms on the main thread right when results
  // should paint. Paying that only in DEV keeps it off every production
  // search's critical path. (Returning ``value`` matches the existing
  // failure-path behavior — callers already cast and treat fields as
  // optional.)
  if (!isDev()) return value;
  const result = schema.safeParse(value);
  if (!result.success) {
    if (isDev()) {
      console.warn(
        `[search-schemas] ${label} failed validation — proceeding with raw value`,
        result.error.issues.slice(0, 5)
      );
    }
    return value;
  }
  return result.data;
}

// ─── PersonRecord (wire shape from /people-search/search) ───────────

const personRecordSchema = z
  .object({
    id: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    full_name: z.string().optional(),
    title: z.string().optional(),
    company_name: z.string().optional(),
    seniority_level: z.string().optional(),
    company_domain: z.string().optional(),
    linkedin_url: z.string().optional(),
    profile_image_url: z.string().optional(),
    headline: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    location: z.string().optional(),
    // ``raw`` is the upstream Crustdata blob — opaque to our schema.
    raw: z.record(z.unknown()).optional(),
  })
  .passthrough();

const personRecordsArraySchema = z.array(personRecordSchema);

export function validatePersonRecords(value: unknown): unknown[] {
  return tolerantParse(
    personRecordsArraySchema,
    value,
    'PersonRecord[]'
  ) as unknown[];
}

// ─── CompanyRecord (wire shape from /companies/search) ──────────────

const companyRecordSchema = z
  .object({
    company_id: z.number().optional(),
    // LinkedIn's internal id, distinct from Crustdata's company_id. The
    // union is not defensiveness: the same field comes back as a number
    // on some endpoints and its string form on others.
    linkedin_id: z.union([z.string(), z.number()]).nullable().optional(),
    company_name: z.string().optional(),
    company_website_domain: z.string().optional(),
    company_website: z.string().optional(),
    company_linkedin_url: z.string().optional(),
    linkedin_profile_url: z.string().optional(),
    company_logo_url: z.string().optional(),
    logo_permalink: z.string().optional(),
    hq_location: z.string().optional(),
    hq_country: z.string().optional(),
    employee_count: z.number().optional(),
    employee_count_range: z.string().optional(),
    linkedin_industries: z.array(z.string()).optional(),
    linkedin_categories: z.array(z.string()).optional(),
    crunchbase_categories: z.array(z.string()).optional(),
    year_founded: z.number().optional(),
    company_type: z.string().optional(),
    estimated_revenue_lower_bound_usd: z.number().optional(),
    estimated_revenue_higher_bound_usd: z.number().optional(),
    linkedin_company_description: z.string().optional(),
    description: z.string().optional(),
    follower_metrics: z.record(z.unknown()).optional(),
    follower_count: z.number().optional(),
    crunchbase_total_investment_usd: z.number().optional(),
    last_funding_round_type: z.string().optional(),
    last_funding_date: z.string().optional(),
    // ``.nullable()`` on the keys declared here: Crustdata sends an
    // explicit ``null`` for a fact it has no answer for rather than
    // omitting the key, and a bare ``.optional()`` rejects null — which
    // would fire the DEV console warning on effectively every search and
    // bury a real schema drift in the noise.
    last_funding_round_investment_usd: z.number().nullable().optional(),
    ipo_date: z.string().nullable().optional(),
    employee_metrics: z.record(z.unknown()).nullable().optional(),
    markets: z.union([z.string(), z.array(z.string())]).optional(),
    stock_symbols: z.array(z.string()).optional(),
    // Crustdata's real keys. ``acquired``/``acquisition_date``/
    // ``investors`` below never appear on the wire and are kept only so
    // cached blobs written before the rename still validate.
    acquisition_status: z.string().nullable().optional(),
    crunchbase_investors: z.array(z.string()).nullable().optional(),
    acquired: z.boolean().optional(),
    acquisition_date: z.string().optional(),
    investors: z.array(z.string()).optional(),
    largest_headcount_country: z.string().optional(),
    updated_at: z.string().optional(),
    twitter_handle: z.string().optional(),
    specialties: z.array(z.string()).optional(),
  })
  .passthrough();

const companyRecordsArraySchema = z.array(companyRecordSchema);

export function validateCompanyRecords(value: unknown): unknown[] {
  return tolerantParse(
    companyRecordsArraySchema,
    value,
    'CompanyRecord[]'
  ) as unknown[];
}

// ─── SavedSearch (from localStorage) ────────────────────────────────

const savedSearchSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    searchType: z.enum(['people', 'company']),
    filters: z.record(z.unknown()),
    createdAt: z.string(),
  })
  .passthrough();

const savedSearchesArraySchema = z.array(savedSearchSchema);

export function validateSavedSearches(value: unknown): unknown[] {
  return tolerantParse(
    savedSearchesArraySchema,
    value,
    'SavedSearch[]'
  ) as unknown[];
}
